/**
 * External Imports
 */
import { FC, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { orderBy } from 'lodash';

/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';

import { RootState } from '../../../../redux/store';
import { useGetSassTokenQuery } from '../../../../redux/apis/Sass.api';

import {
  logListClick,
  logRoundProfile
} from '../../../../utils/analyticsHelpers';
import { useDebounceValue } from '../../../../utils/debouncers';
import {
  date,
  handleBlobPicUrl,
  userProfilePicUrl,
  yearsOfService
} from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { Employee, RoundHistory } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';
import {
  AvatarContainer,
  EmployeeAvatar,
  EmployeeInfoContainer,
  StartDateContainer
} from './CNEdListTable.styles';

/**
 * Static File Imports
 */
import defaultImage from '../../../../assets/images/defaultImage.png';

interface CNEdListTableProps {
  cnedFilter: string;
  cnedRounds: Array<Employee & RoundHistory>;
  adhocRounds: Array<Employee & RoundHistory>;
  isLoading?: boolean;
  searchTerm: string;
  selectedDeptsFromStore: string[];
}

const CNEdListTable: FC<CNEdListTableProps> = ({
  cnedFilter,
  cnedRounds,
  adhocRounds,
  isLoading = false,
  searchTerm,
  selectedDeptsFromStore
}) => {
  const [sortKey, setSortKey] = useState<string>('lastRoundDate');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Last Round');

  const { logTrackingEvent } = useAnalyticsApi();

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { '*': view } = useParams();

  const dbSearchTerm = useDebounceValue(searchTerm, 250);

  const {
    data: sasTokenData,
    // isError: isGetSassTokenError,
    isFetching: isGetSassTokenFetching
    // isLoading: isGetSassTokenLoading
  } = useGetSassTokenQuery();

  const openEmployeeProfile = (employee: Employee) => {
    logTrackingEvent([
      ...logListClick('table', view),
      ...logRoundProfile('cned')
    ]);
    navigate('/support/profile/cned', {
      state: {
        from: pathname,
        employeeId: employee.hcaid,
        mode: undefined
      }
    });
  };

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Employee':
        key = 'fullName';
        break;
      case 'Department':
        key = 'dept';
        break;
      case 'RN Experience':
        key = 'rnExperienceDate';
        break;
      case 'Dept Start':
        key = 'effectiveStartDate';
        break;
      case 'Rounded By':
        key = 'lastRoundedBy';
        break;
      case 'Last Round':
        key = 'lastRoundDate';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const filteredCNEdRounds = useMemo(() => {
    let employeesToShow;
    switch (cnedFilter) {
      case 'no-round':
        employeesToShow = cnedRounds.filter(
          employee =>
            !employee?.lastRoundDate ||
            (employee?.lastRoundDate &&
              date.isOverInDays(employee.lastRoundDate, 30))
        );
        break;
      case 'recently-completed':
        employeesToShow = [
          ...cnedRounds.filter(
            employee =>
              employee?.lastRoundDate &&
              !date.isOverInWeeks(employee.lastRoundDate, 2)
          ),
          ...adhocRounds
        ];
        break;
      case 'new-nurses':
        employeesToShow = cnedRounds.filter(employee =>
          employee.subjectClassifications?.includes('NewNurse')
        );
        break;
      case 'new-to-hca-nurses':
        employeesToShow = cnedRounds.filter(employee =>
          employee.subjectClassifications?.includes('NewToHCA')
        );
        break;
      case 'new-to-dept-nurses':
        employeesToShow = cnedRounds.filter(employee =>
          employee.subjectClassifications?.includes('NewToDepartment')
        );
        break;
      case 'travel-nurses':
        employeesToShow = cnedRounds.filter(() => false);
        break;
      default:
        employeesToShow = cnedRounds;
    }

    const q = new RegExp(dbSearchTerm.trim().replace(/\\/g, ''), 'i');
    return employeesToShow.filter(
      employee =>
        `${employee.firstName} ${employee.lastName}`.match(q) ||
        employee.hcaid.match(q) ||
        `${employee.lastName} ${employee.firstName}`.match(q)
    );
  }, [cnedFilter, cnedRounds, dbSearchTerm, selectedDeptsFromStore]);

  const sortedCNEdRounds = useMemo(() => {
    const filteredCNEdRoundsCopy = [...filteredCNEdRounds];
    const orderBySortDir = sortDir === 'asc' ? 'asc' : 'desc';
    return filteredCNEdRoundsCopy.length > 0
      ? orderBy(
          filteredCNEdRoundsCopy,
          [round => (round as any)[sortKey]?.toLowerCase() || ''],
          [orderBySortDir]
        )
      : filteredCNEdRoundsCopy;
  }, [filteredCNEdRounds, sortDir, sortKey]);

  const isTableEmpty = useMemo(
    () => !dbSearchTerm && sortedCNEdRounds.length === 0,
    [dbSearchTerm, sortedCNEdRounds]
  );

  const isTableSearchEmpty = useMemo(
    () => dbSearchTerm && sortedCNEdRounds.length === 0,
    [dbSearchTerm, sortedCNEdRounds]
  );

  const headers: JSX.Element[] = [
    <CellContainer
      key="Employee"
      id="CNEd-Table-Employee-Column"
      unit="column"
      colWidth={23}
      direction={!(active === 'Employee') ? 'none' : sortDir}
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">Employee</span>
    </CellContainer>,
    <CellContainer
      key="Department"
      id="CNEd-Table-Department-Column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Department') ? 'none' : sortDir}
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">Department</span>
    </CellContainer>,
    <CellContainer
      key="RN Experience"
      id="CNEd-Table-RN-Experience-Column"
      unit="column"
      colWidth={14}
      data-testid="CNEd-Table-RN-Experience-Column-Heading"
      direction={
        !(active === 'RN Experience')
          ? 'none'
          : sortDir === 'asc'
          ? 'desc'
          : 'asc'
      }
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">RN Experience</span>
    </CellContainer>,
    <CellContainer
      key="Dept Start"
      id="CNEd-Table-Dept-Start-Column"
      unit="column"
      colWidth={14}
      direction={!(active === 'Dept Start') ? 'none' : sortDir}
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">Dept Start</span>
    </CellContainer>,
    <CellContainer
      key="Rounded By"
      id="CNEd-Table-Rounded-By-Column"
      unit="column"
      colWidth={17}
      direction={!(active === 'Rounded By') ? 'none' : sortDir}
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">Rounded By</span>
    </CellContainer>,
    <CellContainer
      key="Last Round"
      id="CNEd-Table-Last-Round-Column"
      unit="column"
      colWidth={12}
      direction={!(active === 'Last Round') ? 'none' : sortDir}
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">Last Round</span>
    </CellContainer>
  ];

  const rows = sortedCNEdRounds.map(
    (employee: Employee & RoundHistory, i: number) => (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`CNEd-List-Row-${i}`}
        id={`CNEd-List-Row-${employee.hcaid}`}
        data-testid={`CNEd-List-Row-${employee.hcaid}`}
        unit="row"
        size="default"
        role="button"
        onClick={() => openEmployeeProfile(employee)}
      >
        <CellContainer unit="column" colWidth={23}>
          <EmployeeInfoContainer>
            <AvatarContainer
              className="neu-text-gray-60"
              style={{ height: '40px', width: '40px' }}
            >
              {sasTokenData ? (
                <EmployeeAvatar
                  src={
                    handleBlobPicUrl(
                      employee.pictureUrl,
                      sasTokenData.sasToken
                    ) ??
                    userProfilePicUrl(employee.hcaid, sasTokenData.sasToken)
                  }
                  alt="Employee Image"
                  onError={(e: any) => {
                    e.currentTarget.src = defaultImage;
                    return null;
                  }}
                />
              ) : (
                <EmployeeAvatar src={defaultImage} alt="Default Image" />
              )}
            </AvatarContainer>
            <div>
              <StyledLabel className="neu-text-blue-70 bold">
                {employee.firstName} {employee.lastName}
              </StyledLabel>
              <StyledLabel>{employee.hcaid}</StyledLabel>
            </div>
          </EmployeeInfoContainer>
        </CellContainer>
        <CellContainer unit="column" colWidth={20}>
          <StyledLabel className="neu-text-gray-90">
            {employee.adHoc ? employee.employeeDept : employee.dept}
          </StyledLabel>
        </CellContainer>
        <CellContainer
          unit="column"
          colWidth={14}
          style={{ textAlign: 'left' }}
        >
          <StartDateContainer>
            {employee.rnExperienceDate && employee.rnExperienceDate !== 'NULL'
              ? yearsOfService(employee.rnExperienceDate)
              : 'Unknown'}
          </StartDateContainer>
        </CellContainer>
        <CellContainer unit="column" colWidth={14}>
          <StyledLabel className="d-flex">
            {employee.effectiveStartDate &&
            employee.effectiveStartDate !== 'NULL'
              ? date.datePrettyString(employee.effectiveStartDate)
              : 'Unknown'}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={17}>
          <StyledLabel>
            {employee.lastRoundDate &&
            employee.lastRoundedBy &&
            employee.lastRoundedBy !== 'Unknown'
              ? employee.lastRoundedBy
              : employee.lastRoundDate &&
                employee.lastRoundedBy &&
                employee.lastRoundedBy === 'Unknown'
              ? 'Unknown'
              : ''}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={12}>
          <StyledLabel>
            {employee.lastRoundedBy &&
            employee.lastRoundedBy !== 'Unknown' &&
            (!employee.lastRoundDate ||
              (employee.lastRoundDate &&
                !date.isOrbitTimestamp(employee.lastRoundDate)))
              ? 'Unknown'
              : employee.lastRoundedBy && employee.lastRoundDate
              ? format(date.parseDate(employee.lastRoundDate), 'MM/dd/yyyy') // make unknown if name and no date
              : ''}
          </StyledLabel>
        </CellContainer>
      </StyledNeuTableRow>
    )
  );

  return (
    <Table
      customClass="cned-list-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      isTableEmptySearch={isTableSearchEmpty}
      loading={isLoading || isGetSassTokenFetching}
      type="cnedList"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  selectedDeptsFromStore: state.EmployeeReducer.selectedDepts
});

export default connect(mapReduxStateToProps)(CNEdListTable);
